import {HistoryServiceV2} from '@feature-hub/history-service';
import {
    LayerHandleV2,
    LayerManagerV27,
    ShimBackgroundTypeV2
} from '@volkswagen-onehub/layer-manager';
import * as React from 'react';
import {
    inject,
    postConstruct,
    singleton
} from '../../infrastructure/di/annotations';
import {
    ImageGalleryClickType,
    TrackingService
} from '../tracking/TrackingService';
import {SpaGlobalConfig} from '../../../generated/core';
import {
    FullscreenGalleryLayer,
    FullscreenGalleryLayerCloseButton,
    FullscreenGalleryLayerContent
} from '../../d6/components/fullscreen-gallery-layer';
import {ContainerItem} from '../../infrastructure/ContainerExporter';

type LayerHandleV2Type = LayerHandleV2<{}> | null;

@singleton('FullscreenGalleryLayerService', {env: 'client'})
export class FullscreenGalleryLayerService {
    @inject() private readonly layerManager!: LayerManagerV27;
    @inject() private readonly trackingService!: TrackingService;
    @inject() private readonly historyService!: HistoryServiceV2;
    @inject() private spaGlobalConfig!: SpaGlobalConfig;

    private fullscreenLayer: LayerHandleV2Type = null;

    @postConstruct()
    public initialize(): void {
        this.historyService.rootHistory.listen(_ => {
            this.closeLightbox();
        });
    }

    public openLightbox = async (
        contextData: {value?: string},
        itemElements: JSX.Element[],
        items: ContainerItem[],
        i: number
    ): Promise<void> => {
        if (this.fullscreenLayer) {
            this.closeLightbox();
        }

        const closeButton = (
            <FullscreenGalleryLayerCloseButton
                btnCloseLabel={this.spaGlobalConfig.layerBtnCloseLabel}
                onClick={() => this.closeLightbox()}
            />
        );

        const trackSubNavigation = (type: ImageGalleryClickType) => {
            return (currentIdx: number, _nextIdx: number) => {
                const model = items[currentIdx].model;
                const mediaElementContentId = model.contentId;
                this.trackingService.trackImageSubNavigationClick(
                    type,
                    mediaElementContentId
                );
            };
        };

        this.fullscreenLayer = this.layerManager.openCustomLayer(
            _ => (
                <FullscreenGalleryLayerContent
                    closeButton={closeButton}
                    prevAriaLabel={
                        this.spaGlobalConfig.carouselPreviousSlideLabel
                    }
                    nextAriaLabel={this.spaGlobalConfig.carouselNextSlideLabel}
                    dialogAriaLabel={
                        this.spaGlobalConfig.fullscreenGalleryLabel
                    }
                    index={i}
                    contextData={contextData}
                    onClickImage={trackSubNavigation('media')}
                    onClickNext={trackSubNavigation('next')}
                    onClickPrevious={trackSubNavigation('previous')}
                >
                    {itemElements}
                </FullscreenGalleryLayerContent>
            ),
            {},
            {
                id: 'vwa-cms-fullscreen-gallery-layer',
                userCloseable: true,
                onClose: () => this.onCloseLightbox(),
                shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
                animationTimeout: 0,
                layerComponent: FullscreenGalleryLayer
            }
        );

        this.trackingService.trackFullscreenOpen(
            'fullscreen-gallery-layer-load'
        );
    };

    private readonly onCloseLightbox = (): void => {
        this.trackingService.trackFullscreenClose(
            'fullscreen-gallery-layer-close'
        );
    };

    public readonly closeLightbox = (): void => {
        if (this.fullscreenLayer) {
            this.fullscreenLayer.close();
        }
        this.fullscreenLayer = null;
    };

    public readonly isOpened = (): boolean => {
        return Boolean(
            this.fullscreenLayer && !this.fullscreenLayer.isClosed()
        );
    };
}
